let ISSELECTALLTRUE = 0;

/**
 * Functions related to the customized Kendo Multiselect Autocomplete component. The customizations made to the stock Kendo component
 * are legacy enhancements. Among other changes, the Kendo component was made to resemble the "dropchecks" that were built from
 * scratch and are now generated via various functions within the A11yDropcheck.js file. For this reason, we are referring to these
 * Kendo components as Dropcheck Autocompletes. There were a multitude of a11y issues with the legacy implementation, some of which
 * are fixed here, and some of which are fixed in A11yUtilities.js > _fixKendoAutoCompletesForA11y(), dynamically after the component
 * is rendered (we had to do it this way since this is a a Kendo component and not everything about it can be "fixed" before it is
 * instantiated). In addition, this updated implementation changes the look-and-feel to resemble that of the "new" implementation of
 * our homegrown Dropchecks.
 *
 * NOTE: all of the event handlers for this component are moved into this file as well.
 */
const A11yDropcheckAutocomplete = (function () {

    let _LOG_COLOR = 'blueviolet';

    return {
        LOG_COLOR: _LOG_COLOR,

        create: _create,
        handlerClickCheckbox: _handlerClickCheckbox,
        handlerClickList: _handlerClickList,
        handlerKeydownList: _handlerKeydownList,
        handlerKeydownTextfield: _handlerKeydownTextfield,
        handlerMouseleaveList: _handlerMouseleaveList
    };

    /**
     * This function creates a multiselect kendo autocomplete dropcheck component with an optional "Select All" option. The original
     * version violated a lot of a11y guidelines, so this is a refactored version to fix those. The guts of the original function in
     * GCHomeCommonRef.BindMultiSelectAutoComplete() have been removed from that original function, and a call is made to this new version
     * of the function instead.
     * @param {string} ctrlId - ID of the input element that will be converted to a Kendo Autocomplete Multiselect
     * @param {object} configObj
     *                  url - the url to call to fetch the autocomplete results
     *                  textField - field representing the text to display
     *                  textValue - field representing the ID
     *                  disableSelectAll - flag indicating you want do NOT want to include a "Select All" option (default: false)
     *                  minTextToSearch - minimum num of chars the user must type before search is triggered (default: 3)
     *                  placeholderValue - text to use for placeholder (default: "Begin typing to search")
     *                  callbackFunction - function to call after DDL is completely loaded,
     *                  applyWidth - boolean flag indicating whether a width should be applied (?)
     *                  isHierarchySearch - ??
     */
    function _create(ctrlId, configObj) {
        // unpack the configObj
        let url = configObj.url;
        let ctrlParentName = configObj.ctrlParentName == null || configObj.ctrlParentName === undefined || configObj.ctrlParentName === '' ? 'N/A' : configObj.ctrlParentName;
        let datasourceParamFunction = configObj.datasourceParamFunction;
        let textField = configObj.textField;
        let textValue = configObj.textValue;
        let placeholderValue = configObj.placeholderValue ? configObj.placeholderValue : 'Begin typing to search';
        let minTextToSearch = configObj.minTextToSearch == null || configObj.minTextToSearch === undefined ? 3 : configObj.minTextToSearch;
        let applyWidth = configObj.applyWidth;
        let isHierarchySearch = configObj.isHierarchySearch;
        let disableSelectAll = configObj.disableSelectAll;
        let callbackFunction = configObj.callbackFunction;

        _logDebug('create(): instantiating kendo dropcheck multiselect autocomplete for ID [' + ctrlId + ']', { configObj: configObj });

        // handle to the input text field which user will type in to search
        let inputElem = ctrlParentName !== 'N/A' ? $('#' + ctrlId, '#' + ctrlParentName) : $('#' + ctrlId);
        $(inputElem).attr('tag', ctrlParentName).addClass('autoCompleteMultiSelectTextBox');

        // hidden field which will hold IDs of selected options
        let hdnId = "hdn" + ctrlId;
        $("<input type='hidden' tag='" + placeholderValue + "' id=" + hdnId + " />").insertAfter($(inputElem));

        let dataTextFieldName = "data." + textField;
        let dataTextFieldValue = "data." + textValue;
        let ctrlLabelId = "lbl_" + ctrlId + "_";

        let dataSource = new kendo.data.DataSource({
            type: "json",
            serverFiltering: true,
            transport: {
                read: {
                    type: "POST",
                    url: url,
                    contentType: 'application/json; charset=utf-8',
                    datatype: "json",
                    data: function () {
                        return datasourceParamFunction();
                    }
                },
                parameterMap: function (options) {
                    return JSON.stringify(options);
                }
            }
        });

        let itemTemplate = '';
        itemTemplate += '<label class="autoCompleteMultiSelect" id="' + ctrlLabelId + '#: ' + dataTextFieldValue + ' #" tag=' + ctrlParentName + '>';
        itemTemplate += '<input type="checkbox" id="' + ctrlId + 'chk" #= (data.IsSelected==true || data.Selected==true) ? checked="checked" : "" # style="margin-right: 4px;" />';
        itemTemplate += '<i class="ah-dropcheck-selected fas fa-check" aria-hidden="true"></i>';
        itemTemplate += '<span>#: ' + dataTextFieldName + ' #</span>';
        itemTemplate += '</label>';

        let a11yEnhanceDropdown = function (inputElem) {
            let autoCompleteList = $(inputElem).data('kendoAutoComplete').list;
            if (autoCompleteList) {
                autoCompleteList.parent('.k-animation-container').addClass('KendoMultiSelectAutoComplete autoCompleteMultiSelectTextBox ah-dropcheck-autocomplete');
            }
        }

        $(inputElem).kendoAutoComplete({
            filter: 'startsWith',
            minLength: minTextToSearch,
            placeholder: placeholderValue,
            headerTemplate: '<div class="noDataMessage">No results found</div>',
            dataTextField: textField,
            separator: ', ',
            template: itemTemplate,
            dataSource: dataSource,
            open: function () {
                // let the Kendo animation container popup get rendered, then add our custom CSS classes
                setTimeout(function () {
                    a11yEnhanceDropdown(inputElem);
                });
                A11yForms.adjustAutocomplete(inputElem);
            },
            close: function (e) {
                e.preventDefault();
            },
            change: function (e) {
                if (e.sender._current != null && e.sender._current.length > 0 && $(e.sender._current[0]).find('label').length > 0 && callbackFunction !== undefined) {
                    callbackFunction();
                }
            },
            dataBound: function (e) {
                let noItems = this.list.find(".noDataMessage");
                let noResultWidth = false;

                if (!this.dataSource.view()[0]) {
                    noItems.show();
                    noResultWidth = true;
                    this.popup.open();
                } else {
                    noItems.hide();
                    let count = 0;

                    for (let data of this.dataSource.data()) {
                        if ((data.IsSelected != null && data.IsSelected === true) || (data.Selected != null && data.Selected === true)) {
                            count++;
                        }
                    }

                    let isSelected = this.dataSource.data().length === count;

                    if (JSON.stringify(this.dataSource.data()).indexOf('Select All') === -1 && (disableSelectAll === undefined || !disableSelectAll)) {
                        let selectAllOption = {};
                        selectAllOption[textField] = 'Select All';
                        selectAllOption[textValue] = -1;
                        selectAllOption.IsSelected = isSelected;
                        this.dataSource.insert(0, selectAllOption);
                    }
                }

                let kendoListContainer = e.sender.list.closest(".k-list-container");

                if (noResultWidth) {
                    kendoListContainer.removeClass('kendoAutoCompleteMultiSelect-hierarchySearch');
                } else if (applyWidth !== undefined && applyWidth === true && isHierarchySearch === undefined) {
                    kendoListContainer.addClass('kendoAutoCompleteMultiSelect-width250');
                } else if (isHierarchySearch !== undefined && isHierarchySearch === true) {
                    kendoListContainer.addClass('kendoAutoCompleteMultiSelect-hierarchySearch elgibilityLevelSearch');
                    kendoListContainer.find('li').attr('style', 'border-bottom: 1px solid #cccccc; !important');
                } else {
                    // do nothing - this is here for Sonar
                }

                a11yEnhanceDropdown(inputElem);

                $(document).mousedown(function (e) {
                    if ($('.KendoMultiSelectAutoComplete').has(e.target).length === 0) {
                        if (ctrlId !== '') {
                            let elem = ctrlParentName !== 'N/A' ? $("#" + ctrlId, "#" + ctrlParentName) : $("#" + ctrlId);
                            elem.val('');
                        }
                        $(".KendoMultiSelectAutoComplete").hide();
                    }
                });
            }
        });
    }

    function _handlerClickCheckbox(curObj) {
        _logVerbose('_handlerClickCheckbox()', curObj);

        let selectAlLElem = $(curObj).parent('label[id$="-1"]');

        if (selectAlLElem !== undefined && selectAlLElem.length === 1) {
            let isSelected = $(curObj).prop("checked");
            ISSELECTALLTRUE = isSelected ? 2 : 1;
        }
    }

    function _handlerClickList(curObj) {
        _logVerbose('_handlerClickList()', curObj);

        let parentCtrlId;
        let listElements = $(curObj).find('label');
        let placeholderText = '';

        if ($(listElements).length === 1 && $(curObj).find('label[id$="-1"]').length === 1) {
            let id = $(listElements).attr("id").split("_")[1];
            let chk = $(curObj).find('label[id$="-1"]').find('input[type=checkbox]').prop("checked");
            parentCtrlId = $($(listElements)[0]).attr('tag');
            let inputElem = parentCtrlId !== "N/A" ? $("#" + id, "#" + parentCtrlId) : $("#" + id);
            inputElem.val("");

            if (chk) {
                $("#hdn" + id, " #" + parentCtrlId).val("-1");
                placeholderText = 'All items checked';
            } else {
                $("#hdn" + id, " #" + parentCtrlId).val("");
                placeholderText = $("#hdn" + id).attr('tag');
            }

            $(inputElem).attr('placeholder', placeholderText);
            $('#' + $(inputElem).attr('id') + '_placeholder').text(placeholderText);
        } else if ($(listElements).length > 0 && $($(listElements)[0]).hasClass('autoCompleteMultiSelect')) {
            let labelID = $(curObj).find('label:not([id$="-1"])');
            let labelIDExcept = $(curObj).find('label[id$="-1"]');
            let ctrlValues;
            let ctrlId;

            if (labelID.find('input[type=checkbox]:checked').length === labelID.find('input[type=checkbox]').length) {
                labelIDExcept.find('input[type=checkbox]').prop("checked", true);
            } else {
                labelIDExcept.find('input[type=checkbox]').prop("checked", false);
            }

            parentCtrlId = $($(listElements)[0]).attr('tag');
            ctrlId = $($(listElements)[0]).attr('id');
            let hdnValues = "";

            if (ISSELECTALLTRUE === 2) {
                $($($(listElements)).find('input[type=checkbox]')).prop("checked", true);
            } else if (ISSELECTALLTRUE === 1) {
                $(listElements).find('input[type=checkbox]').prop("checked", false);
            } else {
                // do nothing, this is here for Sonar
            }

            for (let label of labelID) {
                let chk = $($(label).find('input[type=checkbox]')[0]).prop("checked");
                ctrlId = $(label).attr('id');
                ctrlValues = ctrlId.split('_');

                if (chk) {
                    hdnValues = hdnValues === '' ? ctrlValues[2] : hdnValues + ',' + ctrlValues[2];
                } else if ((ctrlValues[1] === 'txtHierarchyLevelSearchValue' || ctrlValues[1] === 'txtCountyLevelSearchValue' || ctrlValues[1] === 'ddlPrimaryCareStaff' || ctrlValues[1] === 'ddlSecondaryCareStaff' || ctrlValues[1] === 'ddlWorkqueueDepartment') && $.fn.getCntrlObject(parentCtrlId, "#hdn" + ctrlValues[1]).val() !== "") {
                    let newValues = $.fn.removeValueFromObj($.fn.getCntrlObject(parentCtrlId, "#hdn" + ctrlValues[1]).val(), ctrlValues[2]);
                    $.fn.getCntrlObject(parentCtrlId, "#hdn" + ctrlValues[1]).val(newValues);
                } else {
                    // do nothing, this is here for Sonar
                }
            }

            ctrlValues = ctrlId.split('_');

            if ((ctrlValues[1] === 'txtHierarchyLevelSearchValue' || ctrlValues[1] === 'txtCountyLevelSearchValue' || ctrlValues[1] === 'ddlPrimaryCareStaff' || ctrlValues[1] === 'ddlSecondaryCareStaff' || ctrlValues[1] === 'ddlWorkqueueDepartment') && $.fn.getCntrlObject(parentCtrlId, "#hdn" + ctrlValues[1]).val() !== "") {
                hdnValues = hdnValues !== "" ? hdnValues + "," + $("#hdn" + ctrlValues[1], " #" + parentCtrlId).val() : $("#hdn" + ctrlValues[1], " #" + parentCtrlId).val();
                hdnValues = $.fn.removeDuplicates(hdnValues);
            }

            if (ctrlValues[1] === 'txtCountyLevelSearchValue' && Array.isArray(hdnValues) && hdnValues.length > 5) {
                if (globalCounty !== undefined && globalCounty !== "") {
                    A11yAlerts.createWarningAlert('#' + globalCounty, 'Maximum of five counties is permitted to be selected.');
                    $('#' + parentCtrlId + ' #txtCountyLevelSearchValue').val('');
                    if (globalCounty === "divParentAdvanceSearch #AlertMessageAdvanceMembersearch") {
                        $(window).scrollTop(0);
                    }
                }
                return false;
            }

            if (parentCtrlId !== undefined && parentCtrlId !== "N/A") {
                $("#hdn" + ctrlValues[1], " #" + parentCtrlId).val(hdnValues);
            } else {
                $("#hdn" + ctrlValues[1]).val(hdnValues);
            }

            let ctrlElem, finalParentCtrlId, finalHdnCtrlId;
            if (parentCtrlId !== 'N/A') {
                ctrlElem = $("#" + ctrlValues[1], "#" + parentCtrlId);
                finalParentCtrlId = "#" + parentCtrlId + " #hdn" + ctrlValues[1];
                finalHdnCtrlId = "#" + parentCtrlId + ctrlValues[1];
            } else {
                ctrlElem = $("#" + ctrlValues[1]);
                finalParentCtrlId = "#hdn" + ctrlValues[1];
                finalHdnCtrlId = "#" + ctrlValues[1];
            }

            if ($(finalParentCtrlId).val().trim().length > 0) {
                if ($.inArray(ctrlValues[1], placeHolderControlTypes) !== '-1') {
                    $(finalHdnCtrlId).removeClass('EmptyPlaceHolder').addClass('SelectedPlaceHolder');
                }

                let length = $(finalParentCtrlId).val().split(',').length;
                placeholderText = length + (length === 1 ? ' item checked' : ' items checked');
            } else {
                if ($.inArray(ctrlValues[1], placeHolderControlTypes) !== '-1') {
                    $(finalHdnCtrlId).addClass('EmptyPlaceHolder').removeClass('SelectedPlaceHolder');
                }

                placeholderText = $(finalParentCtrlId).attr('tag');
            }

            $(ctrlElem).attr('placeholder', placeholderText);
            $('#' + $(ctrlElem).attr('id') + '_placeholder').text(placeholderText);
            $(ctrlElem).val('');

            if (ctrlValues[1] === 'ddlWorkqueueDepartment') {
                GetWQCareStaffList();
            }
        } else {
            // do nothing, this is here for Sonar
        }

        ISSELECTALLTRUE = 0;

        return true;
    }

    /**
     * Keydown event handler for the textfield of the Kendo Autocompletes w multiselect (i.e., the one created from _create())
     * This function was refactored and moved from jQueryExtensions.js so it's in the same file as the function that creates the
     * component (and hence easier to find)
     */
    function _handlerKeydownTextfield(curObj, e) {
        if ((e.keyCode === A11yUtil.KEYCODES.enter || e.keyCode === A11yUtil.KEYCODES.right || e.keyCode === A11yUtil.KEYCODES.down) && !$(".k-animation-container").is(':visible')) {
            _logVerbose('_handlerKeydownTextfield()', curObj);

            let ctrlId = $(curObj).attr('id');
            let parentCtrlId = $(curObj).attr('tag');
            let inputElem = parentCtrlId !== undefined && parentCtrlId !== 'N/A' ? $("#" + ctrlId, " #" + parentCtrlId) : $("#" + ctrlId);
            let autocomplete = $(inputElem).data("kendoAutoComplete");

            // ALERT: the following is legacy code which essentially allows user to bypass the minimum search text length setting (doesn't seem right...)
            if (autocomplete !== undefined && autocomplete != null && (autocomplete.value() == null || autocomplete.value().trim().length === 0)) {
                autocomplete.search('####');
            }

            // fix for the above issue:
            /*  NOSONAR - want to keep this commented code here in case we want to fix the above-mentioned issue
            if (autocomplete !== undefined && autocomplete != null) {
                let searchTextLength = autocomplete.value().trim().length;
                let minLength = autocomplete.options.minLength ? autocomplete.options.minLength : 3;

                if (searchTextLength >= minLength) {
                    autocomplete.search(autocomplete.value().trim());
                }
            }
            */
        }
    }

    /**
     * Keydown event handler for Kendo Autocompletes w multiselect (i.e., the one created from _create())
     * This function was refactored and moved from jQueryExtensions.js so it's in the same file as the function that creates the
     * component (and hence easier to find)
     */
    function _handlerKeydownList(curObj, e) {
        _logVerbose('_handlerKeydownList()', curObj);

        let ctrlId = $($(curObj).parent().children('input[type="hidden"]')).attr('id').replace("hdn", "");
        let inputElem = $("#" + ctrlId).attr('tag') !== "N/A" ? $("#" + ctrlId, "#" + $("#" + ctrlId).attr('tag')) : $("#" + ctrlId);
        let srOnlyPlaceholder = $('#' + ctrlId + '_placeholder');
        let placeholderText = '';

        if ($($(curObj).parent().children()[1]).val() === "-1") {
            placeholderText = 'All items checked';
        } else if ($($(curObj).parent().children()[1]).val().trim().length > 0) {
            let length = $($(curObj).parent().children()[1]).val().split(',').length;
            placeholderText = length + (length === 1 ? ' item checked' : ' items checked');
        } else {
            placeholderText = $($(curObj).parent().children()[1]).attr('tag');
        }

        $(inputElem).attr('placeholder', placeholderText);
        $(srOnlyPlaceholder).text(placeholderText);

        if (e.keyCode === A11yUtil.KEYCODES.enter) {
            let selectedValue = $(inputElem).val().trim().substring(0, $(inputElem).val().trim().length - 1);
            let hdnValues = "";
            let allLabelElements = $(inputElem).data("kendoAutoComplete").ul.closest(".k-animation-container").find('label');
            let listElements = $(inputElem).data("kendoAutoComplete").ul.closest(".k-animation-container").find('label:not([id$="-1"])');

            if (listElements.length === 0) {
                if (selectedValue === 'Select All') {
                    let chk = $($(allLabelElements[0]).find('input[type=checkbox]')).prop("checked");

                    $($(allLabelElements[0]).find('input[type=checkbox]')).prop("checked", !chk);

                    if (!chk) {
                        $("#hdn" + ctrlId, " #" + $("#" + ctrlId).attr('tag')).val("-1");
                        placeholderText = 'All items checked';
                    } else {
                        $("#hdn" + ctrlId, " #" + $("#" + ctrlId).attr('tag')).val("");
                        placeholderText = $("#hdn" + ctrlId).attr('tag');
                    }

                    $(inputElem).attr('placeholder', placeholderText);
                    $(srOnlyPlaceholder).text(placeholderText);
                }
            } else {
                for (let i = 0; i < allLabelElements.length; i++) {
                    let lblValue = $(allLabelElements[i]).text();

                    if (lblValue === selectedValue) {
                        let chk = $($(allLabelElements[i]).find('input[type=checkbox]')[0]).prop("checked");
                        $($(allLabelElements[i]).find('input[type=checkbox]')[0]).prop("checked", !chk);
                        if (selectedValue === 'Select All') {
                            listElements.find('input[type=checkbox]').prop("checked", !chk);
                        }
                    }

                    let chk1 = $($(listElements[i - 1]).find('input[type=checkbox]')[0]).prop("checked");

                    if (chk1) {
                        let ctrlid = $(listElements[i - 1]).attr('id');
                        let ctrlValues = ctrlid.split('_');
                        if (hdnValues === "") {
                            hdnValues = ctrlValues[2];
                        } else {
                            hdnValues = hdnValues + "," + ctrlValues[2];
                        }
                    }
                }

                if (listElements.find('input[type=checkbox]').length === $(inputElem).data("kendoAutoComplete").ul.closest(".k-animation-container").find('label:not([id$="-1"])').find('input[type=checkbox]:checked').length) {
                    $(inputElem).data("kendoAutoComplete").ul.closest(".k-animation-container").find('label[id$="-1"]').find('input[type=checkbox]').prop("checked", true);
                } else {
                    $(inputElem).data("kendoAutoComplete").ul.closest(".k-animation-container").find('label[id$="-1"]').find('input[type=checkbox]').prop("checked", false);
                }

                if ($("#" + ctrlId).attr('tag') !== undefined && $("#" + ctrlId).attr('tag') !== "N/A") {
                    $("#hdn" + ctrlId, " #" + $("#" + ctrlId).attr('tag')).val(hdnValues);
                } else {
                    $("#hdn" + ctrlId).val(hdnValues);
                }

                if ($("#hdn" + ctrlId).val().trim().length > 0) {
                    let length = $("#hdn" + ctrlId).val().split(',').length;
                    placeholderText = length + (length === 1 ? ' item checked' : ' items checked');
                } else {
                    placeholderText = $("#hdn" + ctrlId).attr('tag');
                }

                $(inputElem).attr('placeholder', placeholderText);
                $(srOnlyPlaceholder).text(placeholderText);
            }
        }

        $(inputElem).val('');

        if (e.keyCode === A11yUtil.KEYCODES.tab || e.keyCode === A11yUtil.KEYCODES.escape) {
            $(".k-animation-container").hide();
        }
    }

    function _handlerMouseleaveList(curObj) {
        _logVerbose('_handlerMouseleaveList()', curObj);

        if ($(curObj).find('label').length > 0 && $($(curObj).find('label')[0]).hasClass('autoCompleteMultiSelect')) {
            let labelElem = $($(curObj).find('label')[0]);
            let parentCtrlId = labelElem.attr('tag');
            let ctrlId = labelElem.attr('id');
            let ctrlValues = ctrlId.split('_');
            let inputElem = parentCtrlId !== 'N/A' ? $("#" + ctrlValues[1], "#" + parentCtrlId) : $("#" + ctrlValues[1]);
            let isUmBatchLetterEligibilityChanged = true;
            let isAgBatchLetterEligibilityChanged = true;

            $(inputElem).focus();
            $(inputElem).val('');

            switch ($(inputElem).attr('id')) {
                case 'ddlWLMPlanAC':
                    if (isPlanChecked) {
                        $('#ddlWLMProgramIdval').val("Loading...");
                        CreateDropCheck('ddlWLMProgram', baseURL + '/AssignCareStaff/GetBenProgramsByPlan?planIds=' + $('#hdnddlWLMPlanAC').val(), 'ddlWLMProgramId');
                        $('#ddlWLMProgramIdval').val("Program");
                        isPlanChecked = false;  // NOSONAR - complaining about the need to declare it with "let" (this variable is declared in another file)
                    }
                    break;
                case 'ddlBatchUmEligibilityAC':
                    if (isUmBatchLetterEligibilityChanged) {
                        if ($('#ddlAuthClass').val() !== -1) {
                            createDropCheckSmartSearch('ddlAuthType', baseURL + '/ManageConfig/BindAuthType?ClassID=' + $('#ddlAuthClass').val() + '&LobBenIds=' + $('#hdnddlBatchUmEligibilityAC').val(), 'ddlAuthTypeId', null);
                        }
                        BindGridView();
                    }
                    break;
                case 'ddlBatchAgEligibilityAC':
                    if (isAgBatchLetterEligibilityChanged) {
                        BindGridView();
                    }
                    break;
                default:
                    break;
            }
        }

        $(".KendoMultiSelectAutoComplete").hide();
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yDropcheckAutocomplete', object, _LOG_COLOR);
    }

    function _logVerbose(message, object) {
        DevUtils.logVerbose(message, 'A11yDropcheckAutocomplete', object, _LOG_COLOR);
    }
})();

////////////////////
// Event Handlers //
////////////////////
$(document).off('keydown', '.k-state-focused').on('keydown', '.k-state-focused', function (e) {
    if ((e.keyCode === A11yUtil.KEYCODES.tab || e.keyCode === A11yUtil.KEYCODES.enter || e.keyCode === A11yUtil.KEYCODES.escape) && $(this).hasClass('autoCompleteMultiSelectTextBox')) {
        A11yDropcheckAutocomplete.handlerKeydownList(this, e);
    }
});

$(document).off('keydown', 'input.autoCompleteMultiSelectTextBox').on('keydown', 'input.autoCompleteMultiSelectTextBox', function (e) {
    A11yDropcheckAutocomplete.handlerKeydownTextfield(this, e);
});

$(document).off('mouseleave', ".k-animation-container").on('mouseleave', ".k-animation-container", function () {
    A11yDropcheckAutocomplete.handlerMouseleaveList(this);
});

$(document).off('click', '.k-animation-container input[type="checkbox"]').on('click', '.k-animation-container input[type="checkbox"]', function () {
    A11yDropcheckAutocomplete.handlerClickCheckbox(this);
});

$(document).off('click', '.k-animation-container').on('click', '.k-animation-container', function () {
    return A11yDropcheckAutocomplete.handlerClickList(this);
});

///////////////////////////////////////////////////////////////////////////
// Legacy functions that have been moved to this JS file for convenience //
///////////////////////////////////////////////////////////////////////////
/**
 * @deprecated - call A11yDropcheckAutocomplete.create() instead
 * Function to create the custom dropdown/autocomplete with checkboxes element. This is the legacy function and will make an immediate call
 * to the refactored A11yDropcheckAutocomplete.create() function. This function was moved over from GCHomeCommon.js
 *
 * RE: the NOSONAR comment... Sonar is complaining about too many function arguments, but there are too invocations of this function
 * throughout the app to reasonably change
 */
function BindMultiSelectAutoComplete(url, ctrlId, ctrlParentName, datasourceParamFunction, textField, textValue, placeholderValue, minTextToSearch, callbackFunction, applyWidth, isHierarchySearch, disableSelectAll) {    // NOSONAR
    let configObj = {
        url: url,
        ctrlParentName: ctrlParentName,
        datasourceParamFunction: datasourceParamFunction,
        textField: textField,
        textValue: textValue,
        placeholderValue: placeholderValue,
        minTextToSearch: minTextToSearch,
        applyWidth: applyWidth,
        isHierarchySearch: isHierarchySearch,
        disableSelectAll: disableSelectAll,
        callbackFunction: callbackFunction
    };

    A11yDropcheckAutocomplete.create(ctrlId, configObj);
}
